import React, { useEffect, useState } from "react"
import { ValueWithLabelTemplate } from "../../../../components/layouts/containers/ValueWithLabelElements.container"
import SelectButton from "../../../../components/atoms/SelectButton.atom"
import {
  CLIENT_TEAMS_TABLE_TABS,
  CLIENT_TEAMS_TABLE_TABS_VALUES,
} from "../../../../consts/modules/clients.consts"
import Card from "../../../../components/atoms/Card.atom"
import DataTable from "../../../../components/atoms/table/DataTable.atom"
import Column from "../../../../components/atoms/table/Column.atom"
import { getTeamsHistory } from "../../../../services/clientReport.service"
import useToast from "../../../../hooks/useToast.hook"
import { toastMessages } from "../../../../consts/common.const"

import InputSwitchForm from "../../../../components/molecules/InputSwitchForm.molecule"
import ColumnGroup from "../../../../components/atoms/table/ColumnGroup.atom"
import RowColumnGroup from "../../../../components/atoms/table/RowColumnGroup.atom"
import {
  CLOSE_TYPE_LABELS,
  CLOSE_TYPES,
} from "../../../../consts/modules/clientReport.consts"
import { SPRINT_FILTER_PROPS } from "./SprintsFilter.controller"
import { formatClientsHistoryValues } from "../../../../utils/clientReport.utils"
import IconWithTooltip from "../../../../components/molecules/IconWithTooltip.molecule"

const ClientTeams = ({ selectedPeriod = {} }) => {
  const { clientId } = selectedPeriod
  const { setErrorMessage } = useToast()
  const [selectedTableTab, setSelectedTableTab] = useState(
    CLIENT_TEAMS_TABLE_TABS_VALUES.teams
  )
  const [loading, setLoading] = useState(true)
  const [tableData, setTableData] = useState([])
  const [participantsRegistry, setParticipantsRegistry] = useState([])
  const [showInactiveDevs, setShowInactiveDevs] = useState(false)

  useEffect(() => {
    if (!clientId) {
      setLoading(false)
      return
    }
    getData()
  }, [clientId])

  const getData = async () => {
    setLoading(true)
    try {
      const response = await getTeamsHistory(clientId)
      if (!response.success) {
        setTableData([])
        setParticipantsRegistry([])
        return setErrorMessage({
          message: toastMessages.dataDoesNotLoad.details,
        })
      }
      setTableData(response.result.teamsHistory)
      setParticipantsRegistry(response.result.participantsRegistry)
    } catch {
      setErrorMessage({ message: toastMessages.dataDoesNotLoad.details })
      setTableData([])
      setParticipantsRegistry([])
    } finally {
      setLoading(false)
    }
  }

  const handleTabChange = (e) => {
    if (!e.value) return
    setSelectedTableTab(e.value)
  }

  const isDevParticipant = (participant) =>
    participant.teamAreaId === 1 && !participant.isLead
  const isUxParticipant = (participant) =>
    participant.teamAreaId === 2 && !participant.isLead
  const isLeadParticipant = (participant) => participant.isLead

  const hasNumericInputValue = (rawValue) =>
    rawValue !== null &&
    rawValue !== undefined &&
    rawValue !== "-" &&
    rawValue !== ""

  const getAverageFromValues = (values, shouldCountValue = () => true) => {
    const participation = values.reduce(
      (acc, rawValue) => {
        if (!hasNumericInputValue(rawValue)) return acc

        const numericValue = Number(rawValue)
        if (Number.isNaN(numericValue)) return acc
        if (!shouldCountValue(numericValue)) return acc

        acc.total += numericValue
        acc.count += 1
        return acc
      },
      { total: 0, count: 0 }
    )

    if (participation.count === 0) return null
    return participation.total / participation.count
  }

  const formatAverageValue = (average, nullValue = "-") =>
    average === null ? nullValue : (
      `${formatClientsHistoryValues(average).toString()} hrs`
    )

  const calcAverageHours = (key, nullValue = "-") => {
    if (!tableData.length) return nullValue
    const average = getAverageFromValues(tableData.map((row) => row[key]))
    return formatAverageValue(average, nullValue)
  }

  const getParticipantAverageHoursValue = (participant) => {
    if (!tableData.length) return null

    const { nameTag, isActive } = participant
    return getAverageFromValues(
      tableData.map((row) => row[nameTag]),
      (numericValue) => numericValue > 0 || (numericValue === 0 && !isActive)
    )
  }

  const calcGroupAverageHoursPerSprint = (
    participantsFilter,
    nullValue = "-"
  ) => {
    const participants = participantsRegistry.filter(participantsFilter)
    if (!participants.length || !tableData.length) return nullValue

    const participantTags = participants
      .map((participant) => participant.nameTag)
      .filter(Boolean)

    if (!participantTags.length) return nullValue

    const sprintAverages = tableData
      .map((sprint) => {
        const sprintParticipation = participantTags.reduce(
          (acc, tag) => {
            const rawValue = sprint[tag]
            if (!hasNumericInputValue(rawValue)) return acc

            const numericValue = Number(rawValue)
            if (Number.isNaN(numericValue)) return acc

            acc.total += numericValue
            acc.count += 1
            return acc
          },
          { total: 0, count: 0 }
        )

        if (sprintParticipation.count === 0) return null
        return sprintParticipation.total / sprintParticipation.count
      })
      .filter((average) => average !== null)
    const groupAverage = getAverageFromValues(sprintAverages)
    return formatAverageValue(groupAverage, nullValue)
  }

  const calcParticipantAverageHours = (participant, nullValue = "-") => {
    const participantAverage = getParticipantAverageHoursValue(participant)
    return formatAverageValue(participantAverage, nullValue)
  }

  const visibleParticipants =
    showInactiveDevs ? participantsRegistry : (
      participantsRegistry.filter((participant) => participant.isActive)
    )

  const visibleDevs = visibleParticipants.filter(isDevParticipant)
  const visibleUx = visibleParticipants.filter(isUxParticipant)
  const visibleLeads = visibleParticipants.filter(isLeadParticipant)

  const allParticipants = [...visibleDevs, ...visibleUx, ...visibleLeads]

  const headerGroup = (
    <ColumnGroup>
      <RowColumnGroup>
        <Column header="Sprint" rowSpan={2} {...SPRINT_FILTER_PROPS} />
        <Column
          header={
            <div className="flex gap-3 align-items-center">
              <span>Horas consumidas</span>
              <IconWithTooltip
                tooltipValue={
                  "Las horas consumidas pueden incluir también participantes inactivos"
                }
                iconClassName="pi pi-info-circle"
              />
            </div>
          }
          rowSpan={2}
        />
        {visibleDevs.length > 0 && (
          <Column header="Desarrolladores" colSpan={visibleDevs.length} />
        )}
        {visibleUx.length > 0 && (
          <Column header="UX Team" colSpan={visibleUx.length} />
        )}
        {visibleLeads.length > 0 && (
          <Column header="Leads" colSpan={visibleLeads.length} />
        )}
      </RowColumnGroup>
      <RowColumnGroup>
        {allParticipants.map((p) => (
          <Column
            key={p.nameTag}
            header={p.nameTag}
            className={p.isActive ? "" : "inactive-dev"}
            headerClassName={p.isActive ? "" : "inactive-dev"}
          />
        ))}
      </RowColumnGroup>
    </ColumnGroup>
  )

  return (
    <>
      <div className="client-history-summary-container">
        <Card>
          <div className="flex flex-column font-cairo">
            <p style={{ fontSize: "18px", marginBottom: 7 }}>
              Información general
            </p>
            <div className="flex flex-wrap gap-3">
              <ValueWithLabelTemplate
                label="Promedio de horas dev p/sprint"
                value={calcGroupAverageHoursPerSprint(isDevParticipant, "-")}
                loading={loading}
              />
              <ValueWithLabelTemplate
                label="Promedio de horas ux p/sprint"
                value={calcGroupAverageHoursPerSprint(isUxParticipant, "-")}
                loading={loading}
              />
              <ValueWithLabelTemplate
                label="Promedio de horas leads p/sprint"
                value={calcGroupAverageHoursPerSprint(isLeadParticipant, "-")}
                loading={loading}
              />
            </div>
          </div>
        </Card>
      </div>
      <Card className="mt-3 client-historic-card-container">
        <div className="flex w-full justify-content-between align-items-center mb-4">
          <span style={{ fontSize: "18px" }}>
            Consumo histórico de horas por sprint
          </span>
          <div className="flex gap-3 align-items-center">
            {selectedTableTab ===
              CLIENT_TEAMS_TABLE_TABS_VALUES.participants && (
              <InputSwitchForm
                label="Mostrar desarrolladores inactivos"
                value={showInactiveDevs}
                onChange={() => setShowInactiveDevs(!showInactiveDevs)}
                labelPosition="left"
              />
            )}
            <SelectButton
              options={CLIENT_TEAMS_TABLE_TABS}
              className="client-teams-history-table-selection"
              value={selectedTableTab}
              onChange={handleTabChange}
            />
          </div>
        </div>
        <DataTable
          value={tableData}
          headerColumnGroup={
            selectedTableTab === CLIENT_TEAMS_TABLE_TABS_VALUES.participants ?
              headerGroup
            : null
          }
          scrollable
          className="history-table"
          emptyMessageContent="No hay información de los sprints"
          emptyMessageTitle="Nada por aquí (todavía)"
          loading={loading}
        >
          <Column
            header="Sprint"
            field="sprintNumberByClient"
            footer="Totales"
            {...SPRINT_FILTER_PROPS}
            body={(rowData) => {
              const currSprint = selectedPeriod?.sprintNumber
              return (
                <span
                  className={
                    currSprint === rowData.sprintNumberByClient ?
                      "highlighted-task-column"
                    : ""
                  }
                >
                  {rowData.closeType === CLOSE_TYPES.TIMEOUT ?
                    CLOSE_TYPE_LABELS.TIMEOUT
                  : rowData.sprintNumberByClient}
                </span>
              )
            }}
          />
          {selectedTableTab === CLIENT_TEAMS_TABLE_TABS_VALUES.teams && (
            <Column
              key="totalParticipants"
              header="Participantes"
              field="totalParticipants"
            />
          )}
          <Column
            key="totalSisuHours"
            header="Horas consumidas"
            field="totalSisuHours"
            footer={() => {
              if (!tableData.length) return "-"
              const total = tableData.reduce(
                (acc, curr) => acc + (curr.totalSisuHours || 0),
                0
              )
              return `${formatClientsHistoryValues(total).toString()} hrs`
            }}
          />
          {selectedTableTab === CLIENT_TEAMS_TABLE_TABS_VALUES.teams ?
            [
              <Column
                key="devHours"
                header="Horas totales dev"
                field="devHours"
                footer={calcAverageHours("devHours")}
              />,
              <Column
                key="uxHours"
                header="Horas totales ux"
                field="uxHours"
                footer={calcAverageHours("uxHours")}
              />,
              <Column
                key="leadHours"
                header="Horas totales leads"
                field="leadHours"
                footer={calcAverageHours("leadHours")}
              />,
            ]
          : allParticipants.map((participant) => {
              const columnClass = participant.isActive ? "" : "inactive-dev"
              return (
                <Column
                  key={participant.nameTag}
                  field={participant.nameTag}
                  headerClassName={columnClass}
                  className={columnClass}
                  body={(rowData) => rowData[participant.nameTag] ?? "-"}
                  footer={calcParticipantAverageHours(participant)}
                />
              )
            })
          }
        </DataTable>
      </Card>
    </>
  )
}

export default ClientTeams
